/**
 * Strips out system-generated metadata tags (like Razorpay Payment IDs or Resume Paths)
 * from user-facing notes, personal messages, and request context displays.
 */
export function sanitizeNoteText(text: string | null | undefined): string | null {
  if (!text) return null;
  const cleaned = text
    .replace(/\[\s*Razorpay\s*(?:Payment\s*ID)?\s*:\s*[^\]]+\]/gi, '')
    .replace(/\[\s*Payment\s*ID\s*:\s*[^\]]+\]/gi, '')
    .replace(/\[\s*Resume\s*Path\s*:\s*[^\]]+\]/gi, '')
    .replace(/\[\s*Resume\s*Name\s*:\s*[^\]]+\]/gi, '')
    .replace(/\[\s*Resume\s*File\s*:\s*[^\]]+\]/gi, '')
    .trim();
  return cleaned || null;
}

/**
 * Strips zero-width characters, invisible markers, and normalizes unicode spaces.
 */
export function stripInvisibleCharacters(text: string | null | undefined): string {
  if (!text) return '';
  return String(text)
    // Strip zero-width spaces, BOM, joiners, soft hyphens, and invisible formatting marks
    .replace(/[\u200B-\u200D\uFEFF\u2060\u2063\u00AD\u180E\uFFF9-\uFFFB]/g, '')
    // Replace non-standard whitespace (NBSP, em/en space, thin space, hair space, ideographic space, etc.) with standard space
    .replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000\u2028\u2029]/g, ' ')
    // Collapse multiple horizontal spaces into a single space
    .replace(/[ \t]+/g, ' ')
    .trim();
}

/**
 * Converts text into Title Case while preserving acronyms, initials with periods,
 * and hyphenated or apostrophe names (e.g. "O'Connor", "Mary-Jane", "J.K.").
 */
export function toTitleCase(text: string): string {
  if (!text) return '';
  return text
    .split(' ')
    .map((word) => {
      if (!word) return '';
      // Single letter initial (with or without period) e.g. "A." or "J"
      if (/^[A-Za-z]\.?$/.test(word)) return word.toUpperCase();
      // Well-known short acronyms e.g. "AI", "ML", "CEO", "CTO", "HR", "VP"
      if (/^(AI|ML|CEO|CTO|CFO|COO|VP|HR|UI|UX|AWS|GCP|IIT|NIT|IIM|BITS|MBA|PHD|MD|JD)$/i.test(word)) {
        return word.toUpperCase();
      }
      // Hyphenated names e.g. "Mary-Jane"
      if (word.includes('-')) {
        return word
          .split('-')
          .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : ''))
          .join('-');
      }
      // Apostrophe names e.g. "O'Connor", "D'Souza"
      if (word.includes("'")) {
        return word
          .split("'")
          .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : ''))
          .join("'");
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Repairs names that have been fragmented due to PDF kerning, drop caps,
 * tracking, or AI resume generation artifacts.
 *
 * Handles:
 * - Drop-cap / styled initial patterns: "R ASMI R ANJAN P ATNAIK" -> "Rasmi Ranjan Patnaik"
 * - Multi-space tracked words: "R A S M I   R A N J A N   P A T N A I K" -> "Rasmi Ranjan Patnaik"
 * - Single tracked words: "R A S M I" -> "Rasmi"
 * - Invisible zero-width spaces: "R\u200BASMI" -> "Rasmi"
 * - Preserves legitimate initials: "John F. Kennedy", "John F Kennedy", "A. P. J. Abdul Kalam"
 */
export function cleanExtractedName(rawName: string | null | undefined): string {
  if (!rawName) return '';
  let str = stripInvisibleCharacters(rawName);
  if (!str) return '';

  // 1. Check if name has multi-space separated tracked chunks (e.g. "R A S M I   R A N J A N")
  if (/\s{2,}/.test(rawName)) {
    const chunks = rawName.split(/\s{2,}/).map(stripInvisibleCharacters).filter(Boolean);
    const repairedChunks = chunks.map((chunk) => {
      const tokens = chunk.split(' ');
      if (tokens.length >= 2 && tokens.every((t) => t.length === 1 && /^[A-Za-z]$/.test(t))) {
        return tokens.join('');
      }
      return chunk;
    });
    str = repairedChunks.join(' ');
  }

  // 2. If the entire string is just single letters separated by spaces (e.g. "R A S M I")
  const allTokens = str.split(' ').filter(Boolean);
  if (allTokens.length >= 2 && allTokens.every((t) => t.length === 1 && /^[A-Za-z]$/.test(t))) {
    str = allTokens.join('');
  }

  // 3. Drop-cap split initial pattern (e.g. "R ASMI", "R ANJAN", "P ATNAIK")
  const tokens = str.split(' ').filter(Boolean);
  const merged: string[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const curr = tokens[i];
    const next = tokens[i + 1];

    if (curr.length === 1 && /^[A-Za-z]$/.test(curr)) {
      // Check if previous token was also a single letter (e.g. "A P J") -> sequential initials
      const prev = merged[merged.length - 1];
      const prevWasSingle = prev && prev.length === 1 && /^[A-Za-z]$/.test(prev);

      if (!prevWasSingle && next && /^[A-Za-z]{2,}$/.test(next)) {
        const isNextAllUpper = next === next.toUpperCase();
        const isNextAllLower = next === next.toLowerCase();

        // If next is all uppercase (e.g. "ASMI") or all lowercase ("asmi"), merge with initial
        if (isNextAllUpper || isNextAllLower) {
          merged.push(curr + next);
          i++;
          continue;
        }
      }
    }

    merged.push(curr);
  }

  str = merged.join(' ');

  // 4. Handle remaining regex pattern for split initials
  str = str.replace(/\b([A-Z])\s+([A-Z]{2,})\b/g, '$1$2');

  // 5. Casing normalization: If the entire name is ALL UPPERCASE, convert to Title Case
  if (str.length > 1 && str === str.toUpperCase() && /[A-Z]/.test(str)) {
    str = toTitleCase(str);
  }

  return str.trim();
}

/**
 * Repairs general extracted text (headlines, companies, descriptions) by stripping
 * invisible characters and fixing drop-cap and tracking artifacts.
 */
export function cleanExtractedText(rawText: string | null | undefined): string {
  if (!rawText) return '';
  let str = stripInvisibleCharacters(rawText);
  if (!str) return '';

  // 1. Fix multi-space separated tracked chunks
  if (/\s{2,}/.test(rawText)) {
    const chunks = rawText.split(/\s{2,}/).map(stripInvisibleCharacters).filter(Boolean);
    const repairedChunks = chunks.map((chunk) => {
      const tokens = chunk.split(' ');
      if (tokens.length >= 2 && tokens.every((t) => t.length === 1 && /^[A-Za-z]$/.test(t))) {
        return tokens.join('');
      }
      return chunk;
    });
    str = repairedChunks.join(' ');
  }

  // 2. Token-by-token drop-cap and single-letter reconstruction
  const tokens = str.split(' ').filter(Boolean);
  const merged: string[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const curr = tokens[i];
    const next = tokens[i + 1];

    if (curr.length === 1 && /^[A-Za-z]$/.test(curr)) {
      // If next is also a single letter (e.g. "A" + "I" -> "AI")
      if (next && next.length === 1 && /^[A-Za-z]$/.test(next)) {
        merged.push(curr + next);
        i++;
        continue;
      }
      // If next is multi-letter fragment (e.g. "P" + "RODUCT" -> "PRODUCT")
      if (next && /^[A-Za-z]{2,}$/.test(next)) {
        const isNextAllUpper = next === next.toUpperCase();
        const isNextAllLower = next === next.toLowerCase();
        if (isNextAllUpper || isNextAllLower) {
          merged.push(curr + next);
          i++;
          continue;
        }
      }
    }

    merged.push(curr);
  }

  str = merged.join(' ');

  // 3. Fix any remaining drop-cap splits (e.g. "P RODUCT M ANAGER" -> "PRODUCT MANAGER")
  str = str.replace(/\b([A-Z])\s+([A-Z]{2,})\b/g, '$1$2');

  // 4. If the whole headline/text is ALL-CAPS, convert to Title Case for better presentation
  if (str.length > 2 && str === str.toUpperCase() && /[A-Z]/.test(str) && !str.includes('\n')) {
    str = toTitleCase(str);
  }

  return str.trim();
}

/**
 * Deeply sanitizes all fields of ParsedResumeData.
 */
export function sanitizeExtractedResumeData<T extends Record<string, any>>(data: T): T {
  if (!data || typeof data !== 'object') return data;

  const result: any = Array.isArray(data) ? [] : {};

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) {
      result[key] = value;
      continue;
    }

    if (typeof value === 'string') {
      if (key === 'fullName' || key === 'name') {
        result[key] = cleanExtractedName(value);
      } else if (
        key === 'headline' ||
        key === 'companyName' ||
        key === 'company' ||
        key === 'role' ||
        key === 'designation'
      ) {
        result[key] = cleanExtractedText(value);
      } else {
        result[key] = stripInvisibleCharacters(value);
      }
    } else if (Array.isArray(value)) {
      result[key] = value.map((item) => {
        if (typeof item === 'string') {
          return key === 'skills' ? cleanExtractedText(item) : stripInvisibleCharacters(item);
        }
        if (item && typeof item === 'object') {
          return sanitizeExtractedResumeData(item);
        }
        return item;
      });
    } else if (typeof value === 'object') {
      result[key] = sanitizeExtractedResumeData(value);
    } else {
      result[key] = value;
    }
  }

  return result as T;
}

/**
 * Display guard for a common data-entry typo: "Referer" instead of "Referrer".
 * Matches the standalone misspelling only — the correct "Referrer" is untouched.
 * (The underlying profile data should still be corrected at the source.)
 */
export function fixRefererTypo(text: string | null | undefined): string {
  if (!text) return '';
  return String(text).replace(/\breferer\b/gi, (m) => {
    if (m === m.toUpperCase()) return 'REFERRER';
    if (m[0] === m[0].toUpperCase()) return 'Referrer';
    return 'referrer';
  });
}
